import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Activity, ChevronRight, Trophy } from "lucide-react";
import { api } from "../lib/api";
import { STEPS } from "../data/courseData";
import { ACTIVITY_CATEGORIES, ACTIVITY_TARGET_PER_WEEK } from "../data/activities";

/**
 * Home / Step-Detail mini-card. No upper limit on activities — encourages
 * as many as the user can log. Shows a counter PER CATEGORY plus the total
 * and the running progress toward the 7-completion week-unlock threshold.
 */
export default function DailyPracticeMiniCard({ stepNum, className = "" }) {
  const meta = STEPS.find((s) => s.step === stepNum);
  const [completions, setCompletions] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get("/activity-completions").then((r) => {
      if (cancelled) return;
      setCompletions((r.data || []).filter((c) => c.step === stepNum));
    }).catch(() => {
      if (!cancelled) setCompletions([]);
    });
    return () => { cancelled = true; };
  }, [stepNum]);

  if (completions === null) return null;

  const accent = meta?.accent || "#22D3EE";
  const total = completions.length;
  const unlockPct = Math.min(100, (total / ACTIVITY_TARGET_PER_WEEK) * 100);
  const unlockReached = total >= ACTIVITY_TARGET_PER_WEEK;

  // Per-category counts (only show categories that have at least one log)
  const counts = completions.reduce((acc, c) => {
    const k = c.category || "uncategorized";
    acc[k] = (acc[k] || 0) + 1;
    return acc;
  }, {});
  const breakdown = ACTIVITY_CATEGORIES
    .map((cat) => ({ ...cat, count: counts[cat.id] || 0 }))
    .filter((c) => c.count > 0)
    .sort((a, b) => b.count - a.count);

  return (
    <Link
      to={`/journey/${stepNum}/daily-practice`}
      data-testid={`daily-practice-mini-${stepNum}`}
      className={`block group ${className}`}
    >
      <div
        className="bps-card p-4 hover:-translate-y-0.5 transition-all"
        style={{ borderColor: `${accent}33` }}
      >
        {/* Header */}
        <div className="flex items-center gap-4">
          <div
            className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0"
            style={{
              background: `linear-gradient(140deg, ${accent}, ${accent}55)`,
              border: `1px solid ${accent}`,
              boxShadow: `0 0 22px ${accent}88, inset 0 1px 0 rgba(255,255,255,0.3)`,
            }}
          >
            {unlockReached
              ? <Trophy size={22} strokeWidth={2} className="text-white" style={{ filter: `drop-shadow(0 0 6px ${accent})` }} />
              : <Activity size={22} strokeWidth={2} className="text-white" style={{ filter: `drop-shadow(0 0 6px ${accent})` }} />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="bps-eyebrow" style={{ color: accent }}>Week {stepNum} · Activity log</div>
            <div className="font-display text-base text-white leading-snug mt-0.5">
              {total === 0
                ? "Log your first activity"
                : `${total} ${total === 1 ? "activity" : "activities"} this week`}
            </div>
            <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-[#7C8FA8] mt-0.5">
              {unlockReached
                ? `Week ${stepNum} unlocked · keep going`
                : `${ACTIVITY_TARGET_PER_WEEK - total} more to unlock Week ${Math.min(stepNum + 1, 4)}`}
            </div>
          </div>
          <ChevronRight size={16} className="text-[#7C8FA8] group-hover:text-white transition-colors" />
        </div>

        {/* Unlock progress bar (only until 7) */}
        {!unlockReached && (
          <div className="mt-3 h-1.5 bg-white/5 rounded-full overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${unlockPct}%` }}
              transition={{ duration: 0.9, ease: "easeOut" }}
              className="h-full rounded-full"
              style={{ background: accent, boxShadow: `0 0 10px ${accent}66` }}
            />
          </div>
        )}

        {/* Per-category breakdown — the main visual when there's data */}
        {breakdown.length > 0 && (
          <div className="mt-4 space-y-2" data-testid={`mini-breakdown-${stepNum}`}>
            {breakdown.map((cat) => {
              const widthPct = Math.max(8, (cat.count / Math.max(...breakdown.map((b) => b.count))) * 100);
              return (
                <div key={cat.id} className="flex items-center gap-3" data-testid={`mini-cat-${cat.id}`}>
                  <span className="text-sm leading-none w-5 text-center" aria-hidden>{cat.emoji}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-white/90 truncate">{cat.label}</span>
                      <span className="font-mono text-xs font-semibold" style={{ color: cat.accent }}>{cat.count}</span>
                    </div>
                    <div className="h-1 rounded-full bg-white/5 overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${widthPct}%` }}
                        transition={{ duration: 0.7, ease: "easeOut" }}
                        className="h-full rounded-full"
                        style={{ background: cat.accent, boxShadow: `0 0 6px ${cat.accent}80` }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Link>
  );
}
